/* eslint-disable max-classes-per-file */
import React, { Component } from "react"
import PropTypes from "prop-types"

import {
    enumLabelSort,
    enumLangLabelSort,
    enumOrderSort,
    enumPrecedenceSort,
    enumSingleGenericFilter,
    enumValueSort,
    datePickerGenericFilter,
    textGenericFilter,
} from "shared/search/containers/Filter/Filters"
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    toggleGenericFilter,
} from "shared/search/containers/Filter/FilterCreators"

import {
    ArticleTopicSearchifyFLV,
    CountrySearchifyFLV,
    IssueManagementFilterableListView,
    MediaOutletSearchifyFLV,
    PressContactSearchifyFLV,
    StateSearchifyFLV,
    TrackingDashboardFilterableListView,
    OfficialFilterableWithoutCampaignFinanceListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"
import {
    ArticleTopicSelect,
    CountrySelect,
    CountryEUSelect,
    IssueManagementSelect,
    MediaOutletSelect,
    PressContactSelect,
    StateSelect,
    TrackingDashboardSelect,
    OfficialWithoutCampaignFinanceSelect,
} from "app/static/frontend/selects/containers/PieSelect"

import { allRegionsExceptEU, euRegion } from "shared/imports/regionConstants"
import { MEDIA_FLV } from "shared/imports/sharedConstants"

export class showSyndicatedArticlesFilter extends Component {
    static defaultProps = {
        filterComponent: toggleGenericFilter,
        uniqueFilterId: "showSyndicatedArticlesFilter",
        filterKey: "is_duplicate",
        trueText: "Show",
        falseText: "Hide",
        displayLabel: "Show Syndicated Articles",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

/* -------------
* Type and Topic Filters
------------- */

export class ArticleSourceTypeFilter extends Component {
    static defaultProps = {
        filterComponent: enumGenericFilter,
        uniqueFilterId: "articleSourceType",
        filterKey: "source__media_type__in",
        enumItems: DjangIO.app.media_monitoring.types.MediaSourceType.region_supported_items(),
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export const ArticleTopicFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={ArticleTopicSearchifyFLV} {...props} />
}
ArticleTopicFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleSourceTopic",
    filterKey: "for_article_topics",
    selectComponent: ArticleTopicSelect,
    dataTypeLabel: "Topics",
    displayLabel: "Topics",
}

/* -------------
* General Sources Filters
------------- */

export const ArticleOutletFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={MediaOutletSearchifyFLV} {...props} />
}
ArticleOutletFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleOutlet",
    filterKey: "for_outlet",
    selectComponent: MediaOutletSelect,
    dataTypeLabel: "Outlet",
    displayLabel: "Outlet",
    // Calling this querymethod ensures Media Monitoring and Comms permissioning are applied.
    // When false, we will be able to filter by Outlets - even if we don't have the Comms product.
    searchifyPermanentFilter: { default_for_user: false },
}

export const ArticlePressFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={PressContactSearchifyFLV} {...props} />
}
ArticlePressFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articlePress",
    filterKey: "for_press",
    selectComponent: PressContactSelect,
    dataTypeLabel: "Press",
    displayLabel: "Authors",
    helpText:
        "Results for Press Contacts will return news articles that are matched to the selected press contact. Some news articles may be excluded when using this filter.",
}

export class ArticleAuthorFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: textGenericFilter,
            uniqueFilterId: "articleAuthor",
            filterKey: "author__name",
            placeholderText: "Search name of author...",
            displayLabel: "Search Text of Bylines",
            helpText:
                "Results for Author will return all news articles based on author information, regardless if the author has been mapped to a press contact.",
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticleLanguageFilter extends Component {
    static defaultProps = {
        filterComponent: enumSingleGenericFilter,
        isProtected: true,
        uniqueFilterId: "articleLanguage",
        filterKey: "language",
        enumItems: DjangIO.app.document.types.Language.region_supported_items(),
        sortBy: enumLangLabelSort,
        serialize: (values) => (values && values.length ? values[0].toString() : null),
        deserialize: (value) => {
            if (!value) {
                return null
            }

            return Number.isNaN(value) ? value : parseInt(value)
        },
    }

    render() {
        const FilterComponent = this.props.filterComponent
        // if isProfile is not true, it will be undefined
        // which will be set to default value in FilterComponent
        // but what we want here is false
        const isNullable = this.props.isProfile || false
        return <FilterComponent {...this.props} isNullable={isNullable} />
    }
}

export class ArticleSourceRankFilter extends Component {
    static defaultProps = {
        displayLabel: "Source Rank",
        filterComponent: enumGenericFilter,
        uniqueFilterId: "articleSourceRank",
        filterKey: "source__rank__in",
        enumItems: DjangIO.app.media_monitoring.types.MediaSourceRank.region_supported_items(),
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticleSourceNameFilter extends Component {
    static defaultProps = {
        displayLabel: "Source Name",
        filterComponent: textGenericFilter,
        filterKey: "source__name",
        placeholderText: "Search name of outlet...",
        uniqueFilterId: "articleSourceName",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticleSourceIndustryFilter extends Component {
    static defaultProps = {
        displayLabel: "Source Industry",
        filterComponent: textGenericFilter,
        filterKey: "for_article_source_editorial_topics",
        placeholderText: "Search name of outlet's editorial topics...",
        uniqueFilterId: "articleSourceIndustry",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

/* -------------
* Regions, Title, Mentions
------------- */

export const ArticleCountryFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={CountrySearchifyFLV} {...props} />
}
ArticleCountryFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleCountry",
    filterKey: "for_countries",
    regions: allRegionsExceptEU,
    selectComponent: CountrySelect,
    dataTypeLabel: "Countries",
}

export const ArticleCountryEUFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={CountrySearchifyFLV} {...props} />
}
ArticleCountryEUFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "ArticleCountryEUFilter",
    dataTypeLabel: "Countries",
    filterKey: "for_countries",
    regions: euRegion,
    searchifyPermanentFilter: { only_eu_countries: true },
    selectComponent: CountryEUSelect,
}

export const ArticleStateFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={StateSearchifyFLV} {...props} />
}
ArticleStateFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleState",
    filterKey: "for_states",
    selectComponent: StateSelect,
    dataTypeLabel: "States & Territories",
    displayLabel: "Choose States / Territories",
}

export class ArticleTitleFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: textGenericFilter,
            uniqueFilterId: "articleTitle",
            filterKey: "title_search",
            placeholderText: "Search article headlines...",
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticleOfficialsMentionFilter extends Component {
    static get defaultProps() {
        return {
            filterComponent: searchifySelectGenericFilter,
            uniqueFilterId: "articleOfficialsMentionFilter",
            filterKey: "mentions_officials",
            dataTypeLabel: "Officials",
            searchifyFLV: OfficialFilterableWithoutCampaignFinanceListView,
            selectComponent: OfficialWithoutCampaignFinanceSelect,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

/* -------------
* Date Filters
------------- */

export class ArticlePublishDateListNoFutureFilter extends Component {
    static defaultProps = {
        filterComponent: enumSingleGenericFilter,
        uniqueFilterId: "articleDateListFilter",
        filterKey: "estimated_published_date_range",
        enumItems: DjangIO.app.events.models.DateRangeType.region_supported_items().filter(
            (item) =>
                ![
                    DjangIO.app.events.models.DateRangeType.future.value,
                    DjangIO.app.events.models.DateRangeType.next_month.value,
                    DjangIO.app.events.models.DateRangeType.next_week.value,
                ].includes(item.value),
        ),
        sortBy: enumOrderSort,
        label: "Date Published",
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticlePublishDatePickerStartNoFutureFilter extends Component {
    static defaultProps = {
        filterComponent: datePickerGenericFilter,
        uniqueFilterId: "articleDateStart",
        filterKey: "estimated_published_date__gt",
        displayLabel: "Start Date",
        noFuture: true,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        const minDate = new Date()
        const isFromTrackingDashboard = this.props.searchifyHeaderName === "Tracking Dashboard"
        if (isFromTrackingDashboard) {
            minDate.setFullYear(minDate.getFullYear() - 1)
        } else {
            minDate.setFullYear(minDate.getFullYear() - 2)
        }
        return <FilterComponent {...this.props} minDate={minDate} />
    }
}

export class ArticlePublishDatePickerEndNoFutureFilter extends Component {
    static defaultProps = {
        filterComponent: datePickerGenericFilter,
        uniqueFilterId: "articleDateEnd",
        filterKey: "estimated_published_date__lt",
        displayLabel: "End Date",
        noFuture: true,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        const minDate = new Date()
        const isFromTrackingDashboard = this.props.searchifyHeaderName === "Tracking Dashboard"
        if (isFromTrackingDashboard) {
            minDate.setFullYear(minDate.getFullYear() - 1)
        } else {
            minDate.setFullYear(minDate.getFullYear() - 2)
        }
        return <FilterComponent {...this.props} minDate={minDate} />
    }
}

export class ArticleOutletCoverageFilter extends Component {
    static get defaultProps() {
        return {
            displayLabel: "Outlet Coverage",
            filterComponent: enumGenericFilter,
            uniqueFilterId: "articleOutletCoverageFilter",
            filterKey: "outlet_coverage",
            enumItems: DjangIO.app.media_monitoring.types.MediaSourceCategory.region_supported_items(),
            sortBy: enumLabelSort,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

export class ArticleOutletTypeFilter extends Component {
    static get defaultProps() {
        const { SectorType, MetaOrganizationType, NewMajorOrganizationType } = DjangIO.app.person.newtypes

        return {
            displayLabel: "Outlet Type",
            filterComponent: enumGenericFilter,
            uniqueFilterId: "articleOutletTypeFilter",
            filterKey: "outlet_subtype",
            enumItems: NewMajorOrganizationType.items(),
            enumGroups: MetaOrganizationType.items()
                .filter((item) => item.parent === SectorType.media.value)
                .sort(enumPrecedenceSort)
                .map((item) => ({
                    title: item.label,
                    values: NewMajorOrganizationType.items()
                        .filter((subItem) => subItem.parent === item.value)
                        .sort(enumLabelSort)
                        .map((subItem) => subItem.value),
                    collapseGroup: true,
                })),
            sortBy: enumValueSort,
        }
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}

/* -------------
* Tracking Tool Filters
------------- */

export const ArticleProjectFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={IssueManagementFilterableListView} {...props} />
}
ArticleProjectFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
ArticleProjectFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleProjectFilter",
    filterKey: "for_project_and_subprojects",
    dataTypeLabel: "Issues",
    selectComponent: IssueManagementSelect,
}

export const ArticleTrackingDashboardFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={TrackingDashboardFilterableListView} {...props} />
}
ArticleTrackingDashboardFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    searchifyFLV: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
ArticleTrackingDashboardFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "articleTrackingDashboardFilter",
    filterKey: "for_tracking_dashboards", // TODO: QueryMethod does not exist yet (among others). For tracking tools.
    dataTypeLabel: "Tracking Boards",
    selectComponent: TrackingDashboardSelect,
}

export class ArticleTopicLevelFilter extends Component {
    static defaultProps = {
        displayLabel: "Type",
        filterComponent: enumGenericFilter,
        uniqueFilterId: "articleTopicType",
        filterKey: "topic_type__in",
        enumItems: DjangIO.app.media_monitoring.types.ArticleTopicType.region_supported_items(),
        sortBy: enumValueSort,
    }

    render() {
        const FilterComponent = this.props.filterComponent
        return <FilterComponent {...this.props} />
    }
}
